import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CLEAR_CHAT_CONFIRM } from "@/lib/privacyCopy";
import { Download, Trash2, MessageSquareX } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/Common";
import { LegalLinks } from "@/pages/Legal";

const FIELD_LABELS = { mood: "Mood", energy: "Energy", sleep_hours: "Sleep hours", sleep_quality: "Sleep quality", stress: "Stress", symptoms: "Symptoms", cycle: "Cycle & flow", water_glasses: "Water", movement_minutes: "Movement", note: "Private notes" };
const COUNT_LABELS = { checkins: "Check-ins", insights: "Daily insights", habits: "Habits", reflections: "Reflections", chat_messages: "Conversation messages",
  payment_transactions: "Payment records", push_subscriptions: "Reminder devices", journeys: "Journey", v2_days: "Daily plans", v2_feedback: "Step feedback",
  v2_projects: "Projects", v2_weight: "Weight plan", v2_weighins: "Weigh-ins", v2_wins: "Wins", v2_letters: "Letters", v2_memories: "Things CAMRYN remembers",
  v2_memory_log: "Memory changes", v2_years: "Past years", v2_recaps: "Year recaps", v2_plans: "Saved plans", v2_envelopes: "Daily envelopes", v2_missions: "Weekly missions", v2_experiments: "Experiments", v2_milestones: "Milestones", v2_goals: "Monthly goals", v2_nudges: "Must-do reminders", v2_miss_reasons: "Missed must-do reasons" };

export default function Privacy() {
  const { user, updateProfile, setUser } = useAuth();
  const nav = useNavigate();
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const load = () => api.get("/privacy/summary").then(({ data }) => setSum(data));
  useEffect(() => { load(); }, []);

  const toggleField = async (f) => {
    const fields = user.ai_fields.includes(f) ? user.ai_fields.filter((x) => x !== f) : [...user.ai_fields, f];
    await updateProfile({ ai_fields: fields });
  };
  const exportData = async () => {
    const { data } = await api.get("/privacy/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    Object.assign(document.createElement("a"), { href: url, download: `camryn-export-${data.exported_at.slice(0, 10)}.json` }).click();
    URL.revokeObjectURL(url);
    toast.success("Your data has been downloaded");
  };
  const clearChat = async () => {
    if (!window.confirm(CLEAR_CHAT_CONFIRM)) return;
    try { await api.delete("/privacy/chat"); load(); toast.success("Chat history deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteAccount = async () => {
    try { await api.post("/privacy/delete-account", { password: pw }); setUser(false); nav("/"); toast.success("Your account and all data were permanently deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageTitle eyebrow="Privacy Center" title="You're in control" sub="See what CAMRYN stores, choose what the AI can see, and take or delete your data whenever you like. Export and deletion are always free." />

      <section className="card p-5 rise" data-testid="privacy-stored-summary">
        <div className="eyebrow mb-4">What we store</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {sum && Object.entries(sum.counts).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-[#F5F2EC] p-3"><div className="font-display text-2xl" data-testid={`privacy-count-${k}`}>{v}</div><div className="text-xs text-stone-500">{COUNT_LABELS[k]}</div></div>
          ))}
        </div>
      </section>

      <section className="card p-5 mt-4 rise d1">
        <div className="flex items-center justify-between mb-1">
          <div><div className="font-medium">AI features</div><div className="text-xs text-stone-500">Insights, reflections and Companion. Off means simple rule-based tips only.</div></div>
          <Switch aria-label="Let CAMRYN's AI use my data" data-testid="privacy-ai-consent-switch" checked={user.ai_consent} onCheckedChange={(v) => updateProfile({ ai_consent: v })} />
        </div>
        {user.ai_consent && (
          <div className="mt-5 pt-5 border-t border-stone-100">
            <div className="eyebrow mb-3">Fields the AI may read</div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {Object.entries(FIELD_LABELS).map(([k, label]) => (
                <label key={k} className="flex items-center justify-between text-sm">{label}
                  <Switch aria-label={`Share ${k.replace(/_/g, " ")} with the AI`} data-testid={`privacy-field-${k}`} checked={user.ai_fields.includes(k)} onCheckedChange={() => toggleField(k)} /></label>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="mt-4 space-y-2.5 rise d2">
        <button onClick={exportData} data-testid="privacy-export-json-btn" className="card w-full p-4 flex items-center gap-3 text-left hover:border-stone-300 chip">
          <Download className="h-5 w-5 text-[#6E8B74]" /><span><span className="block font-medium">Export all my data</span><span className="block text-xs text-stone-500">Everything, as a JSON file</span></span>
        </button>
        <button onClick={clearChat} data-testid="privacy-clear-chat-btn" className="card w-full p-4 flex items-center gap-3 text-left hover:border-stone-300 chip">
          <MessageSquareX className="h-5 w-5 text-[#D99B52]" /><span><span className="block font-medium">Delete chat history</span><span className="block text-xs text-stone-500">Every message and the chat summary. Memories stay in Memory Vault</span></span>
        </button>
        <button onClick={() => setOpen(true)} data-testid="privacy-delete-account-btn" className="card w-full p-4 flex items-center gap-3 text-left border-red-200 hover:bg-red-50 chip">
          <Trash2 className="h-5 w-5 text-red-600" /><span><span className="block font-medium text-red-700">Delete my account permanently</span><span className="block text-xs text-stone-500">All data is erased. This can't be undone.</span></span>
        </button>
      </section>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="privacy-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete everything?</AlertDialogTitle>
            <AlertDialogDescription>Your account, check-ins, insights, habits, reflections and conversations will be permanently erased. If you have Plus, cancel it first. Enter your password to confirm.</AlertDialogDescription>
          </AlertDialogHeader>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="privacy-delete-password-input" placeholder="Password" className="h-11 px-4 rounded-xl border border-stone-200" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="privacy-delete-cancel-btn">Keep my account</AlertDialogCancel>
            <button onClick={deleteAccount} disabled={!pw} data-testid="privacy-delete-confirm-btn" className="h-10 px-4 rounded-md bg-red-600 text-white text-sm font-semibold disabled:opacity-50">Delete permanently</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <p className="text-xs text-stone-500 mt-8" data-testid="privacy-legal-links">Read the details: <Link to="/privacy-promise" data-testid="privacy-promise-link" className="tap underline mr-4">How CAMRYN protects you</Link><LegalLinks className="ml-1" /></p>
    </div>
  );
}
